import { MeetingFormValues } from "@/pages/workflows/edit/actions/meeting/workflow-meeting.schema.ts";
import { getApiWorkflowScheduledEntityTypeSelectItems } from "@/pages/workflows/edit/pages/workflow-edit-pages.tsx";
import { useWorkflows } from "@/pages/workflows/workflow-hook.tsx";
import {
  ApiWorkflowMeetingDateReference,
  ApiWorkflowTriggerScheduleEntityField,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { Box, Input, Select, SelectItem, Stack } from "@skailer-com/uikit";
import { Control, Controller, FieldErrors, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

const MEETING_DATE_OFFSET_MAX = 90;

interface MeetingDateFieldProps {
  control: Control<MeetingFormValues>;
  errors: FieldErrors<MeetingFormValues>;
}

export const MeetingDateField = ({ control, errors }: MeetingDateFieldProps) => {
  const { t } = useTranslation();
  const { getWorkflowTriggerScheduleDirectionOptions, getWorkflowTriggerScheduleDayTypeOptions } =
    useWorkflows();

  const reference = useWatch({ control, name: "meeting_date_reference" });

  const daysField = (
    <Controller
      control={control}
      name={"meeting_date_offset"}
      render={({ field: { value, onChange } }) => (
        <Input
          type={"number"}
          label={t("common:days")}
          min={0}
          max={MEETING_DATE_OFFSET_MAX}
          value={Number.isNaN(value) ? "" : String(value)}
          onChange={(event) => {
            const next = event.target.valueAsNumber;
            onChange(Number.isNaN(next) ? NaN : Math.max(0, next));
          }}
          error={errors.meeting_date_offset?.message}
        />
      )}
    />
  );

  const referenceOptions: SelectItem[] = [
    {
      id: ApiWorkflowMeetingDateReference.block_execution,
      title: t("workflow:meeting_date_reference_block_execution"),
    },
    {
      id: ApiWorkflowMeetingDateReference.entity_date,
      title: t("workflow:meeting_date_reference_entity_date"),
    },
  ];

  const entityDateOptions: SelectItem[] = getApiWorkflowScheduledEntityTypeSelectItems().map(
    (item) => ({ id: item.field, title: item.title })
  );

  return (
    <Stack>
      <Controller
        control={control}
        name={"meeting_date_reference"}
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("workflow:meeting_date_label")}
            value={value}
            emptyTitle={t("workflow:meeting_date_label")}
            options={referenceOptions}
            onChange={onChange}
          />
        )}
      />

      {reference === ApiWorkflowMeetingDateReference.entity_date && (
        <Controller
          control={control}
          name={"meeting_date_entity_field"}
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("workflow:meeting_date_entity_label")}
              value={value}
              emptyTitle={t("workflow:meeting_date_entity_label")}
              options={entityDateOptions}
              onChange={(next) =>
                onChange((next as ApiWorkflowTriggerScheduleEntityField | null) ?? null)
              }
              error={errors.meeting_date_entity_field?.message}
            />
          )}
        />
      )}

      {reference === ApiWorkflowMeetingDateReference.entity_date ? (
        <Box className={"grid grid-cols-2 gap-xs"}>
          <Controller
            control={control}
            name={"meeting_date_direction"}
            render={({ field: { value, onChange } }) => (
              <Select
                label={t("workflow:meeting_date_direction_label")}
                value={value}
                emptyTitle={t("workflow:meeting_date_direction_label")}
                options={getWorkflowTriggerScheduleDirectionOptions()}
                onChange={onChange}
              />
            )}
          />
          {daysField}
        </Box>
      ) : (
        daysField
      )}

      <Controller
        control={control}
        name={"meeting_date_day_type"}
        render={({ field: { value, onChange } }) => (
          <Select
            label={t("workflow:meeting_date_day_type_label")}
            value={value}
            emptyTitle={t("workflow:meeting_date_day_type_label")}
            options={getWorkflowTriggerScheduleDayTypeOptions()}
            onChange={onChange}
          />
        )}
      />
    </Stack>
  );
};
